import { FilterCheckboxListType } from '@bytel/product-wall/types';

import { getWallConfig } from '@services/config';
import { getProductStock, productDetailsService } from '@services/products';

import {
    SapiMergedSapicType,
    SapiProductChildType,
    SapiProductChildTypeWithSapicInfo,
    SapiProductType,
} from '@app-types/api/sapi';
import { WallConfigType, WallType } from '@app-types/config';
import {
    BaseProductFull,
    CharacteristicsChildProduct,
    FeatureType,
    ProductChildFull,
    ProductFeatureType,
    ProductParentFull,
    ProductStockDetails,
    ProductType,
} from '@app-types/product';
import { DeliveryContextType } from '@app-types/product-delivery';

import { roundStringNumber } from '@helpers/number';
import { getActiveFilters } from '@helpers/seo';

import { stockConfig } from '@constants/stock';

import { getStockOptions, getStockStatus, isOutOfStock } from './stock';

export const getCurrentProduct = async (
    product: ProductParentFull,
    urlProduct: string,
    deliveryContext?: DeliveryContextType,
): Promise<{ product: ProductParentFull; currentProduct: ProductChildFull }> => {
    const childs = Object.entries(product.childs);
    const stockCache = new Map<string, Awaited<ReturnType<typeof productDetailsService.getProductStock>>>();

    const getStockForChild = async (gencode: string) => {
        if (!stockCache.has(gencode)) {
            const stockDetails = await productDetailsService.getProductStock(gencode, deliveryContext);
            stockCache.set(gencode, stockDetails);
        }
        return stockCache.get(gencode)!;
    };

    const matchingChild = childs.find(([, child]) => child.urlKey === urlProduct);

    if (matchingChild) {
        const [gencode, child] = matchingChild;
        const childWithStock = { ...child, stockDetails: await getStockForChild(gencode) };

        return {
            product: {
                ...product,
                childs: {
                    ...product.childs,
                    [gencode]: childWithStock,
                },
            },
            currentProduct: childWithStock,
        };
    }

    for (const [gencode, child] of childs) {
        const childWithStock = { ...child, stockDetails: await getStockForChild(gencode) };

        if (!isOutOfStock(childWithStock.stockDetails.stock.status)) {
            return {
                product: {
                    ...product,
                    childs: {
                        ...product.childs,
                        [gencode]: childWithStock,
                    },
                },
                currentProduct: childWithStock,
            };
        }
    }

    const [firstGencode, firstChild] = childs[0]!;
    const firstChildWithStock = { ...firstChild, stockDetails: stockCache.get(firstGencode)! };

    return {
        product: {
            ...product,
            childs: {
                ...product.childs,
                [firstGencode]: firstChildWithStock,
            },
        },
        currentProduct: firstChildWithStock,
    };
};

export function transformChildProductToParentProduct(childProduct: SapiProductChildType): SapiProductType {
    return {
        ...childProduct,
        childs: {
            [childProduct.gencode]: childProduct,
        },
        product_ids: [childProduct.gencode],
        parentGencode: childProduct.gencode,
    };
}

function getConnectionRange(connection: string | undefined) {
    if (connection) {
        return `${connection.toUpperCase()}${connection === '5g' ? '/4G+' : ''}`;
    }
    return undefined;
}

export const sapiProductMapper = (sapiProduct: SapiMergedSapicType): ProductParentFull => {
    const featuresRules: Record<string, string[]> = {};

    Object.values(sapiProduct.childs).forEach((child) => {
        if (child.color && child.capacity) {
            if (!featuresRules[child.color]) {
                featuresRules[child.color] = [];
            }

            if (!featuresRules[child.color]?.includes(child.capacity)) {
                featuresRules[child.color]?.push(child.capacity);
            }
        }
    });

    return {
        ...mapCommonAttributes(sapiProduct),
        productIds: sapiProduct.product_ids,
        childs: getChilds(sapiProduct),
        features: {
            color: getFeatures(sapiProduct.childs, ProductFeatureType.COLOR, 'color_code'),
            capacity: getFeatures(sapiProduct.childs, ProductFeatureType.CAPACITY, 'capacity'),
        },
        featuresRules,
    };
};

const mapCommonAttributes = (
    product: SapiMergedSapicType | SapiProductChildTypeWithSapicInfo,
    isChild?: boolean,
): BaseProductFull => {
    const odr = product.odr && isChild ? Number.parseInt(product.odr.amount) : 0;

    const prices = isChild
        ? {
              initial: product.price,
              withOdr: product.price - odr,
              odr,
          }
        : {
              initial: product.price,
          };

    const sapiInfo = {
        name: product.name,
        image: product.image,
        hasFunding: false,
        availableCnc: product.available_click_and_collect || false,
        recommended: false,
    };

    const mappedSapicAttributes = product.productSapic!;

    return {
        ...sapiInfo, // this is overcharged by mappedSapicAttributes if product.productSapic exists
        ...mappedSapicAttributes,
        gencode: product.gencode,
        campaignId: product.campaign_id,
        das: getDas(product),
        ecoTax: product.ecotax && product.ecotax !== '0' ? roundStringNumber(product.ecotax, 2) : undefined,
        entityId: product.entity_id,
        manufacturer: product.manufacturer,
        manufacturerCode: product.manufacturer_code,
        medias: product.medias,
        metaDescription: product.meta_description ?? '',
        packContent: product.pack_content,
        price: mappedSapicAttributes.price,
        prices,
        reparability: mapReparapility(product),
        sku: product.sku,
        title: product.title ?? `${product.manufacturer} ${product.name}`.trim(),
        type: getProductType(product.type_id as ProductType),
        urlKey: product.url_key,
        videoUrl: product.url_video,
    };
};

const mapReparapility = (product: Partial<SapiMergedSapicType>) => {
    const note = Number.parseFloat(product?.note_reparation ?? '-1');
    if (note < 0) {
        return undefined;
    }

    return {
        note,
        pdf: product.note_reparation_pdf,
    };
};

const mapCharacteristics = (
    product: SapiMergedSapicType,
    productChild: SapiProductChildTypeWithSapicInfo,
): CharacteristicsChildProduct => ({
    weight: roundStringNumber(product.weight, 3),
    length: roundStringNumber(product.length, 3),
    width: roundStringNumber(product.width, 3),
    depth: roundStringNumber(product.depth, 3),
    height: roundStringNumber(product.height, 3),
    diagonal: roundStringNumber(product.diagonal, 3),
    battery: {
        capacity: roundStringNumber(product.battery_capacity, 3),
        isRemovable: product.battery_removable,
        talkTime: product.battery_talk_time,
        standbyTime: product.battery_standby_time,
    },
    screenTechnology: product.screen_technology,
    material: product.material,
    resolution: {
        width: roundStringNumber(product.resolution_width, 0),
        height: roundStringNumber(product.resolution_height, 0),
        sensor: roundStringNumber(product.resolution_sensor, 0),
    },
    os: {
        name: product.os,
        version: product.os_version,
    },
    wifi: product.wifi,
    connectionRange: getConnectionRange(product.max_connection),
    isSimMultiple: product.sim_multiple,
    isESimCompatible: ['esim', 'hybride'].includes(product.sim_type ?? ''),
    hasAutoFocus: product.auto_focus,
    isDoubleCamera: product.double_camera,
    capacityAvailable: product.capacity_available,
    capacitySd: product.capacity_sd,
    processor: {
        core: product.processor_core,
        speed: product.processor_speed,
    },
    included: productChild.included,
    nonIncluded: productChild.non_included,
    charging:
        product?.charging_head !== undefined
            ? {
                  head: product.charging_head,
                  maxLoadPower: product.max_load_power
                      ? Number.parseFloat(product.max_load_power).toString()
                      : undefined,
                  minLoadPower: product.min_load_power
                      ? Number.parseFloat(product.min_load_power).toString()
                      : undefined,
              }
            : undefined,
    energyLabel: product.energy_label
        ? {
              label: product.energy_label,
              img: product.energy_label_img ?? '',
              pdf: product.energy_label_pdf ?? '',
          }
        : undefined,
    output: {
        memoryCard: productChild.memory_card_output,
        charger: productChild.charger_output,
        audio: productChild.audio_output,
    },
});

const getProductType = (type: ProductType): ProductType => {
    switch (type) {
        case ProductType.ACCESSORY:
        case ProductType.ACCESSORY_CONFIGURABLE:
            return ProductType.ACCESSORY;
        case ProductType.PHONE:
        case ProductType.PHONE_SIMPLE:
            return ProductType.PHONE;
        default:
            return type;
    }
};

const getDas = (product: Partial<SapiMergedSapicType>): { name: string; value: string }[] => {
    const das: { name: string; value: string }[] = [];

    const sar = roundStringNumber(product.sar, 3, true);
    if (sar) {
        das.push({ name: 'tête', value: sar });
    }

    const trunkSar = roundStringNumber(product.trunk_sar, 3, true);
    if (trunkSar) {
        das.push({ name: 'tronc', value: trunkSar });
    }

    const limbsSar = roundStringNumber(product.limbs_sar, 3, true);
    if (limbsSar) {
        das.push({ name: 'membres', value: limbsSar });
    }

    return das;
};

const getFeatures = (
    childs: SapiProductType['childs'],
    feature: ProductFeatureType,
    value: keyof SapiProductChildType,
): FeatureType[] => {
    const featuresMap = new Map<string, string>();

    Object.values(childs).forEach((child) => {
        const featureName = child[feature];
        const featureValue = child[value];

        if (featureName && !featuresMap.has(featureName) && featureValue && typeof featureValue === 'string') {
            featuresMap.set(featureName, featureValue);
        }
    });

    if (feature === ProductFeatureType.CAPACITY) {
        return sortCapacities(Array.from(featuresMap.values())).map((value) => ({ name: value, value }));
    }

    return Array.from(featuresMap, ([name, value]) => ({ name, value }));
};

export function getChilds(product: SapiMergedSapicType): Record<string, ProductChildFull> {
    return Object.values(product.childs).reduce(
        (accum, child) => {
            accum[child.gencode] = {
                ...mapCommonAttributes(child, true),
                characteristics: mapCharacteristics(product, child),
                colorCode: child.color_code,
                color: child.color,
                capacity: child.capacity,
                parentTitle: product.title ?? `${product.manufacturer} ${product.name}`.trim(),
                isOnlyWeb: product.exclu_web,
                quantity: child.qty,
            };
            return accum;
        },
        {} as Record<string, ProductChildFull>,
    );
}

export async function getChildStock(
    product: SapiProductChildType,
    deliveryContext?: DeliveryContextType,
): Promise<ProductStockDetails> {
    const productsResa = product.exclu_web
        ? { isEresa: false, isPreresa: false }
        : await getProductStock(product.gencode);

    const {
        is_preorder: isPreorder,
        in_stock: isInStock,
        available_soon: isAvailableSoon,
        soon_exhausted: isSoonExhausted,
    } = product;
    const stock =
        stockConfig[getStockStatus(productsResa.isEresa, isPreorder, isInStock, isAvailableSoon, isSoonExhausted)];
    const stockOptions = await getStockOptions(stock, product, productsResa, deliveryContext);

    return {
        ...stockOptions,
        stock,
    };
}

function sortCapacities(capacities: string[]): string[] {
    return capacities.sort((a, b) => {
        const regex = /(\d+)\s*(Go|To|Mo)/;
        const matchA = a.match(regex);
        const matchB = b.match(regex);

        const convertToMo = (match: RegExpMatchArray | null) => {
            if (!match) return 0;
            const value = Number.parseInt(match[1] as string);
            switch (match[2]) {
                case 'To':
                    return value * 1024 * 1024;
                case 'Go':
                    return value * 1024;
                case 'Mo':
                    return value;
                default:
                    return 0;
            }
        };

        return convertToMo(matchA) - convertToMo(matchB);
    });
}

export function revalidateH2Counter() {
    const h2 = document.querySelectorAll('.product-characteristics h2') as NodeListOf<HTMLElement>;
    h2.forEach((h) => {
        h.style.counterIncrement = 'none';
        requestAnimationFrame(() => {
            h.style.counterIncrement = 'h2';
        });
    });
}

export function shouldRedirectToFilteredWall(
    slug: string,
    brandsWithFilter: string[],
    wallConfig: WallConfigType,
): { shouldRedirect: boolean; brand: string } {
    const brandKey = getActiveFilters(wallConfig, slug).marque?.[0];

    if (slug.includes('reconditionne') && brandKey && reconditionedBrandMap[brandKey]) {
        return { shouldRedirect: true, brand: reconditionedBrandMap[brandKey] };
    }

    if (brandKey && brandsWithFilter.includes(brandKey)) {
        return { shouldRedirect: true, brand: brandKey };
    }
    return { shouldRedirect: false, brand: '' };
}

const reconditionedBrandMap: Record<string, string> = {
    'apple-iphone': 'iphone-reconditionne',
    samsung: 'telephone-samsung-reconditionne',
};

export function handleProductNotFound(slug: string, wallType: WallType, defaultPath: string) {
    const wallConfig = getWallConfig(wallType);
    const brandsWithFilter =
        (wallConfig.filtersObject?.marque as FilterCheckboxListType | undefined)?.values.map((brand) => brand.urlKey) ??
        [];
    const { shouldRedirect, brand } = shouldRedirectToFilteredWall(slug, brandsWithFilter, wallConfig);

    if (shouldRedirect) {
        return { redirect: 'permanent', path: `${defaultPath}/filtre/${brand}` };
    } else {
        return { redirect: 'permanent', path: defaultPath };
    }
}
